import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getNoticeDetails } from '../../../redux/actions/SocietyPortal/NoticeAction/NoticeAction';
import { getSocietyProfile } from '../../../redux/actions/SocietyPortal/SocietyProfileAction/SocietyProfileAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import {
    AUDIENCE_LABEL,
    humanize,
    fmtDateTime,
    fmtDate,
    authorName,
} from './noticeConstants';

// Printable notice — the version a society pins to the physical board.
//
// Deliberately plain: no cards, no colour fills, black on white so it survives
// a mono office printer. Everything interactive is marked .no-print.
const PrintNotice = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { noticeDetailsData: notice, noticeDetailsLoading } = useSelector(
        state => state.NoticeReducer || {}
    );
    const societyProfile = useSelector(
        state => state.SocietyProfileReducers?.societyProfileData || null
    );

    useEffect(() => {
        if (id) dispatch(getNoticeDetails({ id, societyId }));
    }, [dispatch, id, societyId]);

    useEffect(() => {
        if (societyId && !societyProfile) dispatch(getSocietyProfile({ society_id: societyId }));
    }, [dispatch, societyId, societyProfile]);

    if (noticeDetailsLoading || !notice) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
                <div className="tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-sky-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4 tw-text-sm">Loading notice...</p>
                </div>
            </div>
        );
    }

    const kind = notice.notice_type === 'CIRCULAR' ? 'Circular' : 'Notice';
    const societyName = societyProfile?.name || societyProfile?.society_name || '';

    const audienceLine = notice.audience === 'CUSTOM'
        ? `${(notice.recipients || []).length} specific member(s)`
        : notice.audience === 'FLATS'
            ? (notice.flats || []).map(f => `Flat ${f.flat_number}`).join(', ') || 'Specific flats'
            : (AUDIENCE_LABEL[notice.audience] || humanize(notice.audience));

    return (
        <>
            <style>{`
                @media print {
                    body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print { display: none !important; }
                    .print-page { margin: 0; padding: 10mm; box-shadow: none !important; border: none !important; }
                    @page { size: A4; margin: 12mm; }
                }
                .notice-body { font-size: 13px; line-height: 1.65; }
                .notice-body p { margin: 0 0 10px; }
                .notice-body ul, .notice-body ol { margin: 0 0 10px 20px; }
                .notice-body table { border-collapse: collapse; width: 100%; }
                .notice-body table td, .notice-body table th { border: 1px solid #999; padding: 4px 6px; }
            `}</style>

            <div className="tw-min-h-screen tw-bg-gray-100 tw-py-6">
                {/* Toolbar */}
                <div className="no-print tw-max-w-[210mm] tw-mx-auto tw-mb-4 tw-flex tw-items-center tw-justify-between tw-px-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800"
                    >
                        <Icon icon="mdi:arrow-left" /> Back
                    </button>
                    <button
                        onClick={() => window.print()}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md"
                    >
                        <Icon icon="mdi:printer-outline" className="tw-text-base" />
                        Print
                    </button>
                </div>

                <div className="print-page tw-max-w-[210mm] tw-mx-auto tw-bg-white tw-shadow-sm tw-p-10">
                    {/* Letterhead */}
                    <div className="tw-text-center tw-border-b tw-border-black tw-pb-3 tw-mb-5">
                        {societyName && (
                            <h1 className="tw-text-lg tw-font-bold tw-uppercase tw-tracking-wide">{societyName}</h1>
                        )}
                        <p className="tw-text-[11px] tw-uppercase tw-tracking-[0.2em] tw-mt-1">{kind}</p>
                    </div>

                    {/* Reference line — the bit that makes a circular quotable in minutes */}
                    <div className="tw-flex tw-justify-between tw-text-[11px] tw-mb-5">
                        <span>Ref: <strong>{notice.notice_code || '—'}</strong></span>
                        <span>
                            Date: <strong>{fmtDate(notice.published_at || notice.created_at)}</strong>
                        </span>
                    </div>

                    <h2 className="tw-text-base tw-font-bold tw-text-center tw-mb-1 tw-underline">
                        {notice.title}
                    </h2>
                    {notice.summary && (
                        <p className="tw-text-xs tw-text-center tw-italic tw-mb-4">{notice.summary}</p>
                    )}

                    <div
                        className="notice-body tw-mt-4"
                        dangerouslySetInnerHTML={{ __html: notice.body || '' }}
                    />

                    {Array.isArray(notice.attachments) && notice.attachments.length > 0 && (
                        <div className="tw-mt-6 tw-text-[11px]">
                            <p className="tw-font-semibold tw-mb-1">Enclosures:</p>
                            <ol className="tw-list-decimal tw-ml-5">
                                {notice.attachments.map(a => <li key={a.attachment_id}>{a.file_name}</li>)}
                            </ol>
                        </div>
                    )}

                    {/* Signature block */}
                    <div className="tw-mt-12 tw-flex tw-justify-end">
                        <div className="tw-text-center tw-text-[11px]">
                            <div className="tw-border-t tw-border-black tw-pt-1 tw-px-8">
                                <p className="tw-font-semibold">{authorName(notice.createdBy)}</p>
                                <p>For {societyName || 'the Society'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Footer meta */}
                    <div className="tw-mt-10 tw-pt-2 tw-border-t tw-border-gray-400 tw-text-[10px] tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-1">
                        <span>Addressed to: {audienceLine}</span>
                        <span>Category: {humanize(notice.category)}</span>
                        {notice.expires_at && <span>Valid until: {fmtDateTime(notice.expires_at)}</span>}
                    </div>
                </div>
            </div>
        </>
    );
};

export default PrintNotice;
